/**
 * SmsAdminPage — minimal admin entry for SMS templates + send logs (Issue #196).
 *
 * Codex audit fix: provides the long-overdue Admin-Web entry that maps to the
 * existing `/api/v1/admin/sms/*` endpoints. Operators can now manage templates
 * and inspect the sanitised send log without a database session.
 *
 * 红线:
 *   - 全部数据通过 `@jinzhuan/api-client` 类型化客户端拉取；禁止手写 fetch /
 *     伪造前端状态。
 *   - 列表行只展示后端脱敏后的字段（phoneMask / errorCode / providerMessageId）；
 *     永远不展示 OTP / 完整手机号 / provider 原始 payload。
 *   - 写权限 (`sms_templates:write`) 缺失时，创建 / 更新表单被禁用，按钮显示
 *     无权限提示，避免渲染会触发 403 的按钮。
 */
import { useMemo, useState, type FormEvent, type ReactElement, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/hooks/useAuth';
import {
  seedDefaultTemplates,
  useSmsSendLogs,
  useSmsTemplates,
  type CreateSmsTemplateRequest,
  type SmsDefaultSeedResult,
  type SmsScene,
  type SmsSendLogDto,
  type SmsSendResult,
  type SmsTemplateDto,
  type SmsTemplateStatus,
  type UpdateSmsTemplateRequest,
} from '@/hooks/useSms';
import { canWriteSmsTemplates } from '@/lib/smsPermissions';
import {
  SMS_SCENE_LABELS,
  SMS_TEMPLATE_STATUS_LABELS,
  SMS_SEND_RESULT_LABELS,
  formatEnumLabel,
} from '@/lib/enumLabels';

const SMS_SCENE_OPTIONS: ReadonlyArray<{ value: CreateSmsTemplateRequest['scene']; label: string }> = [
  { value: 'admin_login', label: '后台登录' },
  { value: 'admin_sensitive', label: '后台敏感操作' },
  { value: 'admin_password_reset', label: '后台密码重置' },
  { value: 'customer_login', label: '客户登录' },
  { value: 'customer_bind_phone', label: '客户绑定手机号' },
  { value: 'customer_password_reset', label: '客户密码重置' },
  { value: 'high_risk_login_step_up', label: '高风险登录加固' },
];

const TEMPLATE_STATUS_OPTIONS: ReadonlyArray<{
  value: NonNullable<UpdateSmsTemplateRequest['status']>;
  label: string;
}> = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '启用' },
  { value: 'disabled', label: '禁用' },
  { value: 'archived', label: '归档' },
];

const SEND_RESULT_OPTIONS: ReadonlyArray<{ value: SmsSendLogDto['result']; label: string }> = [
  { value: 'queued', label: '已入队' },
  { value: 'sent', label: '已发送' },
  { value: 'delivered', label: '已送达' },
  { value: 'failed', label: '失败' },
  { value: 'rate_limited', label: '被限流' },
  { value: 'rejected', label: '前置拒绝' },
];

export default function SmsAdminPage(): ReactElement {
  const { permissions } = useAuth();
  const canWrite = canWriteSmsTemplates(permissions);

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="sms-admin-page">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">短信模板与发送日志</h1>
        <p className="text-sm text-muted-foreground">
          管理短信模板的草稿 / 启用 / 禁用状态，并通过脱敏发送日志排查投递问题。
          列表只展示后端脱敏后的 phoneMask 与 providerMessageId；不展示验证码或
          完整手机号。
        </p>
      </div>
      <TemplatesCard canWrite={canWrite} />
      <SendLogsCard />
    </div>
  );
}

// ----- Templates -----------------------------------------------------------

interface TemplatesCardProps {
  readonly canWrite: boolean;
}

function TemplatesCard({ canWrite }: TemplatesCardProps): ReactElement {
  const [sceneFilter, setSceneFilter] = useState<SmsScene | ''>('');
  const [statusFilter, setStatusFilter] = useState<SmsTemplateStatus | ''>('');
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState<SmsDefaultSeedResult | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const filters = useMemo(
    () => ({
      ...(sceneFilter !== '' ? { scene: sceneFilter } : {}),
      ...(statusFilter !== '' ? { status: statusFilter } : {}),
    }),
    [sceneFilter, statusFilter],
  );
  const state = useSmsTemplates(filters);

  const refresh = state.refresh;
  const handleSeedDefaults = useCallback(async () => {
    setSeedBusy(true);
    setSeedResult(null);
    setSeedError(null);
    try {
      const result = await seedDefaultTemplates();
      setSeedResult(result);
      await refresh();
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : '导入默认模板失败');
    } finally {
      setSeedBusy(false);
    }
  }, [refresh]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">短信模板</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="sms-template-scene">场景</Label>
            <select
              id="sms-template-scene"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={sceneFilter}
              onChange={(e) => setSceneFilter(e.target.value as SmsScene | '')}
              data-testid="sms-template-scene-filter"
            >
              <option value="">全部</option>
              {SMS_SCENE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="sms-template-status">状态</Label>
            <select
              id="sms-template-status"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SmsTemplateStatus | '')}
              data-testid="sms-template-status-filter"
            >
              <option value="">全部</option>
              {TEMPLATE_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <Button variant="ghost" onClick={() => void state.refresh()} data-testid="sms-template-refresh">
            刷新
          </Button>
        </div>

        {canWrite ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={seedBusy}
                onClick={() => void handleSeedDefaults()}
                data-testid="sms-seed-defaults-button"
              >
                {seedBusy ? '导入中…' : '导入默认模板'}
              </Button>
              {seedResult ? (
                <span className="text-xs text-muted-foreground" data-testid="sms-seed-result">
                  已新建 {seedResult.created} 个，跳过 {seedResult.skipped} 个（共 {seedResult.total} 个默认模板）
                </span>
              ) : null}
              {seedError ? (
                <span className="text-xs text-destructive" data-testid="sms-seed-error">
                  {seedError}
                </span>
              ) : null}
            </div>
            <CreateTemplateForm onCreate={state.createTemplate} />
          </>
        ) : (
          <div
            className="rounded-md border border-dashed border-muted-foreground/30 p-3 text-xs text-muted-foreground"
            data-testid="sms-template-no-write-permission"
          >
            当前账户无 sms_templates:write 权限，无法创建或修改模板。
          </div>
        )}

        <TemplatesTable
          templates={state.data ?? []}
          loading={state.loading}
          error={state.error}
          canWrite={canWrite}
          onUpdate={state.updateTemplate}
        />
      </CardContent>
    </Card>
  );
}

interface CreateTemplateFormProps {
  readonly onCreate: (input: CreateSmsTemplateRequest) => Promise<SmsTemplateDto | null>;
}

function CreateTemplateForm({ onCreate }: CreateTemplateFormProps): ReactElement {
  const [templateKey, setTemplateKey] = useState('');
  const [scene, setScene] = useState<CreateSmsTemplateRequest['scene']>('admin_login');
  const [signature, setSignature] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('您的验证码是 {{code}}，{{ttlMinutes}} 分钟内有效。');
  const [providerTemplateCode, setProviderTemplateCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onCreate({
        templateKey: templateKey.trim(),
        scene,
        signature: signature.trim(),
        bodyTemplate: bodyTemplate.trim(),
        ...(providerTemplateCode.trim() !== ''
          ? { providerTemplateCode: providerTemplateCode.trim() }
          : {}),
      });
      setTemplateKey('');
      setSignature('');
      setProviderTemplateCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建模板失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      onSubmit={handleSubmit}
      data-testid="sms-template-create-form"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="sms-template-key">模板 key（租户内唯一）</Label>
        <Input
          id="sms-template-key"
          value={templateKey}
          onChange={(e) => setTemplateKey(e.target.value)}
          placeholder="default:admin_login"
          required
          data-testid="sms-template-key-input"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="sms-template-scene-create">场景</Label>
        <select
          id="sms-template-scene-create"
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={scene}
          onChange={(e) => setScene(e.target.value as CreateSmsTemplateRequest['scene'])}
        >
          {SMS_SCENE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="sms-template-signature">签名（短信前缀）</Label>
        <Input
          id="sms-template-signature"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="JinzhuanCell"
          required
          data-testid="sms-template-signature-input"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="sms-template-provider-code">Provider 模板编号（如 Aliyun SMS_xxx）</Label>
        <Input
          id="sms-template-provider-code"
          value={providerTemplateCode}
          onChange={(e) => setProviderTemplateCode(e.target.value)}
          placeholder="SMS_999999（可选）"
          data-testid="sms-template-provider-code-input"
        />
      </div>
      <div className="flex flex-col gap-1 sm:col-span-2">
        <Label htmlFor="sms-template-body">正文模板（仅允许占位符 {`{{code}}`} / {`{{ttlMinutes}}`}）</Label>
        <Input
          id="sms-template-body"
          value={bodyTemplate}
          onChange={(e) => setBodyTemplate(e.target.value)}
          required
          data-testid="sms-template-body-input"
        />
      </div>
      {error ? (
        <div className="text-sm text-destructive sm:col-span-2" data-testid="sms-template-create-error">
          {error}
        </div>
      ) : null}
      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} data-testid="sms-template-create-submit">
          {submitting ? '创建中…' : '创建模板（默认 draft）'}
        </Button>
      </div>
    </form>
  );
}

interface TemplatesTableProps {
  readonly templates: readonly SmsTemplateDto[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canWrite: boolean;
  readonly onUpdate: (id: string, input: UpdateSmsTemplateRequest) => Promise<SmsTemplateDto | null>;
}

function TemplatesTable({ templates, loading, error, canWrite, onUpdate }: TemplatesTableProps): ReactElement {
  if (loading) return <div className="text-sm text-muted-foreground">加载中…</div>;
  if (error)
    return (
      <div className="text-sm text-destructive" data-testid="sms-template-list-error">
        {error}
      </div>
    );
  if (templates.length === 0)
    return <div className="text-sm text-muted-foreground">暂无模板，请先创建。</div>;
  return (
    <Table data-testid="sms-template-table">
      <TableHeader>
        <TableRow>
          <TableHead>模板 key</TableHead>
          <TableHead>场景</TableHead>
          <TableHead>签名</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>Provider 模板</TableHead>
          <TableHead>版本</TableHead>
          <TableHead>更新时间</TableHead>
          <TableHead className="text-right">操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {templates.map((tpl) => (
          <TableRow key={tpl.id} data-testid={`sms-template-row-${tpl.id}`}>
            <TableCell className="font-mono text-xs">{tpl.templateKey}</TableCell>
            <TableCell>{formatEnumLabel(tpl.scene, SMS_SCENE_LABELS, '未知场景')}</TableCell>
            <TableCell>{tpl.signature}</TableCell>
            <TableCell>{formatEnumLabel(tpl.status, SMS_TEMPLATE_STATUS_LABELS, '未知状态')}</TableCell>
            <TableCell className="font-mono text-xs">{tpl.providerTemplateCode ?? '—'}</TableCell>
            <TableCell className="text-right">{tpl.version}</TableCell>
            <TableCell className="text-xs">{new Date(tpl.updatedAt).toLocaleString()}</TableCell>
            <TableCell className="text-right">
              <StatusToggleButtons canWrite={canWrite} template={tpl} onUpdate={onUpdate} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface StatusToggleButtonsProps {
  readonly canWrite: boolean;
  readonly template: SmsTemplateDto;
  readonly onUpdate: (id: string, input: UpdateSmsTemplateRequest) => Promise<SmsTemplateDto | null>;
}

function StatusToggleButtons({ canWrite, template, onUpdate }: StatusToggleButtonsProps): ReactElement {
  const [busy, setBusy] = useState(false);
  if (!canWrite) {
    return <span className="text-xs text-muted-foreground">只读</span>;
  }
  const transitions: ReadonlyArray<{ label: string; status: NonNullable<UpdateSmsTemplateRequest['status']> }> = (() => {
    if (template.status === 'draft') return [{ label: '启用', status: 'active' }];
    if (template.status === 'active') return [{ label: '禁用', status: 'disabled' }];
    if (template.status === 'disabled') return [
      { label: '启用', status: 'active' },
      { label: '归档', status: 'archived' },
    ];
    return [];
  })();
  if (transitions.length === 0) {
    return <span className="text-xs text-muted-foreground">终态</span>;
  }
  return (
    <div className="flex justify-end gap-2">
      {transitions.map((t) => (
        <Button
          key={t.status}
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onUpdate(template.id, { status: t.status });
            } finally {
              setBusy(false);
            }
          }}
          data-testid={`sms-template-status-to-${t.status}-${template.id}`}
        >
          {t.label}
        </Button>
      ))}
    </div>
  );
}

// ----- Send logs -----------------------------------------------------------

function SendLogsCard(): ReactElement {
  const [sceneFilter, setSceneFilter] = useState<SmsScene | ''>('');
  const [resultFilter, setResultFilter] = useState<SmsSendResult | ''>('');
  const filters = useMemo(
    () => ({
      ...(sceneFilter !== '' ? { scene: sceneFilter } : {}),
      ...(resultFilter !== '' ? { result: resultFilter } : {}),
    }),
    [sceneFilter, resultFilter],
  );
  const state = useSmsSendLogs(filters);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">短信发送日志（脱敏）</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="sms-log-scene">场景</Label>
            <select
              id="sms-log-scene"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={sceneFilter}
              onChange={(e) => setSceneFilter(e.target.value as SmsScene | '')}
              data-testid="sms-log-scene-filter"
            >
              <option value="">全部</option>
              {SMS_SCENE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="sms-log-result">结果</Label>
            <select
              id="sms-log-result"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value as SmsSendResult | '')}
              data-testid="sms-log-result-filter"
            >
              <option value="">全部</option>
              {SEND_RESULT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <Button variant="ghost" onClick={() => void state.refresh()} data-testid="sms-log-refresh">
            刷新
          </Button>
        </div>

        {state.loading ? (
          <div className="text-sm text-muted-foreground">加载中…</div>
        ) : state.error ? (
          <div className="text-sm text-destructive" data-testid="sms-log-error">
            {state.error}
          </div>
        ) : !state.data || state.data.length === 0 ? (
          <div className="text-sm text-muted-foreground">暂无日志。</div>
        ) : (
          <Table data-testid="sms-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>时间</TableHead>
                <TableHead>场景</TableHead>
                <TableHead>模板</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>手机号（脱敏）</TableHead>
                <TableHead>结果</TableHead>
                <TableHead>Provider 消息 ID</TableHead>
                <TableHead>错误代码</TableHead>
                <TableHead className="text-right">耗时（毫秒）</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-xs">{new Date(row.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{formatEnumLabel(row.scene, SMS_SCENE_LABELS, '未知场景')}</TableCell>
                  <TableCell className="font-mono text-xs">{row.templateKey ?? '—'}</TableCell>
                  <TableCell>{row.provider}</TableCell>
                  <TableCell className="font-mono text-xs">{row.phoneMask}</TableCell>
                  <TableCell>{formatEnumLabel(row.result, SMS_SEND_RESULT_LABELS, '未知结果')}</TableCell>
                  <TableCell className="font-mono text-xs">{row.providerMessageId ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{row.errorCode ?? '—'}</TableCell>
                  <TableCell className="text-right">{row.latencyMs ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
